import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import moment from 'moment';
import {toast} from 'react-toastify';
import * as Actions from '../../../store/actions';
import EasyLookupModals from '../EasyLookupModals';
import {
  DashboardLayout,
  PageLimit,
  Pagination,
  PrimaryButton,
  TableLayout,
  EmptyState,
  Loader,
  SecondaryButton,
} from '../../../components';
import FilterV2 from '../../../components/FilterV2';

import {corruptedFile, eye, lookupEmpty} from '../../../assets/images/images';
import {
  capitalizeEveryWord,
  checkStatus,
  Constants,
  mapToOptionsArray,
  parseUrlParams,
} from '../../../helpers';
import {getCookieValue} from '../../../helpers/getCookie';
import {PAGES_URL} from '../../../helpers/constants';
import {
  tableHeader as batchBusinessTableHeader,
  getIDType,
} from '../LookupUtils';
import {useApps} from '../../../hooks/useAllApps';
import {OverviewCards} from '../../../components/OverviewCards';
import {buildTableCardData} from '../../../helpers/buildTableCardData';
import Breadcrumbs from 'components/BreadCrumbs';
import PageNumber from '../../../components/PageNumber';

function BusinessBatchLookup({
  children,
  getBusinessBatchLookup,
  getBatch,
  changeLookupPage,
  getApiLogs,
  lookup: {batchBusinessLookup, h_loading, loading},
  auth: {userDetails},
  apiLogs: {logs, loading: apiLoading},
}) {
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [openBatchLookup, setOpenBL] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentFilters, setCurrentFilters] = useState({
    apps: undefined,
    status: undefined,
    country: undefined,
  });

  const {allAppsData} = useApps();
  const env = getCookieValue('env');

  const filterDefinitions = useMemo(() => {
    const definitions = [];

    if (allAppsData && allAppsData.length > 0) {
      definitions.push({
        key: 'apps',
        label: 'Apps',
        type: 'select',
        options: mapToOptionsArray(allAppsData),
        multiSelect: true,
      });
    }

    // definitions.push({
    //   key: 'status',
    //   label: 'Status',
    //   type: 'select',
    //   options: Constants.lookups.status,
    //   multiSelect: false,
    // });
    //
    // definitions.push({
    //   key: 'country',
    //   label: 'Country',
    //   type: 'select',
    //   options: COUNTRIES,
    //   multiSelect: true,
    // });
    return definitions;
  }, [allAppsData]);

  const handleFilterChange = newValuesFromFilterV2 => {
    setData([]);
    setCurrentFilters(() => ({...newValuesFromFilterV2}));
    setCurrentPage(1);
    const queryParams = new URLSearchParams();
    Object.entries(newValuesFromFilterV2).forEach(([key, value]) => {
      if (['status', 'country'].includes(key)) {
        if (Array.isArray(value) && value.length > 0) {
          queryParams.set(key, value.join(','));
        } else if (!Array.isArray(value) && value) {
          queryParams.set(key, value);
        }
      }
    });
    if (searchTerm) queryParams.set('search', searchTerm);
    queryParams.set('page', '1');
    navigate(`?${queryParams.toString()}`, {replace: true});
  };

  const pageClick = selected => {
    setCurrentPage(selected);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', selected.toString());
    navigate(`?${searchParams.toString()}`, {replace: true});
  };

  const hasActiveFilters = useMemo(
    () =>
      Object.entries(currentFilters).some(([key, value]) => {
        if (['apps', 'status', 'country'].includes(key)) {
          if (Array.isArray(value)) return value.length > 0;
          return value !== undefined && value !== '';
        }
        return false;
      }),
    [currentFilters],
  );

  useEffect(() => {
    if (env === 'Sandbox') {
      Object.values(userDetails?.permissions?.logPermissions || {}).some(
        value => value,
      ) && getApiLogs({page: currentPage, limit});
    } else if (!children) {
      const apiParams = {
        limit,
        page: currentPage,
        company_id: userDetails?.company?.id,
        search: searchTerm || undefined,
      };

      if (currentFilters.apps && currentFilters.apps.length > 0) {
        apiParams.apps = currentFilters.apps;
      } else if (
        allAppsData &&
        allAppsData.length > 0 &&
        !currentFilters.apps
      ) {
        apiParams.apps = allAppsData.map(app => app._id);
      }

      if (currentFilters.status && currentFilters.status.length > 0) {
        apiParams.status = currentFilters.status.join(',');
      } else if (currentFilters.status) {
        apiParams.status = currentFilters.status;
      }

      if (currentFilters.country && currentFilters.country.length > 0) {
        apiParams.country = currentFilters.country.join(',');
      }

      if (
        currentFilters.dateRange &&
        currentFilters.dateRange.start &&
        currentFilters.dateRange.end
      ) {
        apiParams.start = currentFilters.dateRange.start;
        apiParams.end = currentFilters.dateRange.end;
      }

      const searchParamsFromUrl = new URLSearchParams(window.location.search);
      const pageParam = searchParamsFromUrl.get('page');
      if (pageParam && parseInt(pageParam) !== currentPage) {
        setCurrentPage(parseInt(pageParam));
        apiParams.page = parseInt(pageParam);
      }

      if (userDetails?.company?.id && apiParams.apps?.length > 0) {
        getBusinessBatchLookup(apiParams);
      }
    }
  }, [
    allAppsData,
    currentPage,
    limit,
    userDetails?.company?.id,
    userDetails?.permissions?.logPermissions,
    searchTerm,
    currentFilters,
    env,
    getBusinessBatchLookup,
    getApiLogs,
    children,
    navigate,
  ]);

  useEffect(() => {
    const params = parseUrlParams();
    const initialFiltersState = {};
    if (params.apps) initialFiltersState.apps = params.apps.split(',');
    if (params.status) initialFiltersState.status = params.status.split(',');
    if (params.country) initialFiltersState.country = params.country.split(',');
    if (params.search) setSearchTerm(params.search);
    if (params.page) setCurrentPage(parseInt(params.page));

    setCurrentFilters(prev => ({...prev, ...initialFiltersState}));
  }, []);

  const SANDBOX_DATA = useMemo(() => {
    if (!apiLoading && logs && logs.api_logs) {
      return logs.api_logs.map((log, index) => ({
        id: `sandbox_batch_${index}`,
        name: `Business_Batch_File_${index + 1}.csv`,
        model_type: 'business_tin_search_batch',
        lookup_type: 'business_tin_search_batch',
        status:
          index % 3 === 0
            ? 'completed'
            : index % 3 === 1
            ? 'processing'
            : 'failed',
        date_created: log?.createdAt,
        country: 'NG',
      }));
    }
    return [];
  }, [logs, apiLoading]);

  useEffect(() => {
    if (env === 'Sandbox' && !apiLoading && logs) {
      setData(SANDBOX_DATA);
    } else {
      !h_loading &&
        batchBusinessLookup &&
        setData([...(batchBusinessLookup?.records || [])]);
    }
  }, [env, batchBusinessLookup, apiLoading, SANDBOX_DATA, h_loading, logs]);

  const handleExport = () => {
    const apiParams = {
      limit: batchBusinessLookup?.totalRecords || 10000,
      page: 1,
      company_id: userDetails?.company?.id,
      search: searchTerm || undefined,
      download: true,
    };

    if (currentFilters.apps && currentFilters.apps.length > 0) {
      apiParams.apps = currentFilters.apps;
    } else if (allAppsData && allAppsData.length > 0) {
      apiParams.apps = allAppsData.map(app => app._id);
    }

    if (currentFilters.status && currentFilters.status.length > 0) {
      apiParams.status = currentFilters.status.join(',');
    } else if (currentFilters.status) {
      apiParams.status = currentFilters.status;
    }

    if (currentFilters.country && currentFilters.country.length > 0) {
      apiParams.country = currentFilters.country.join(',');
    }
    const cleanedApiParams = Object.fromEntries(
      Object.entries(apiParams).filter(([, v]) => v !== undefined),
    );

    if (userDetails?.company?.id && cleanedApiParams.apps) {
      getBusinessBatchLookup(cleanedApiParams);
      // toast.info("Export started. You'll be notified when it's ready.");
    } else {
      toast.error('Cannot start export without company and app context.');
    }
  };

  const {performBatchBusinessLookup, downloadBusinessLookup} =
    useMemo(() => userDetails?.permissions?.lookupPermissions, [userDetails]) ||
    {};
  const performRight =
    userDetails?.permissions?.role === 'Owner'
      ? false
      : !performBatchBusinessLookup;

  const EmptyStateComponent = () => {
    const isLoading = h_loading || (env === 'Sandbox' && apiLoading);
    const isDisabled = !userDetails?.company?.verified || performRight;
    const isNoDataInSandbox =
      env === 'Sandbox' && !apiLoading && data?.length === 0;
    const isEmptyArray = !h_loading && !apiLoading && data?.length === 0;

    if (isLoading) {
      return <Loader height={50} />;
    }

    const commonEmptyStateProps = {
      onClick: () => setOpenBL(true),
      src: lookupEmpty,
      disabled: isDisabled,
      style: {cursor: isDisabled ? 'not-allowed' : 'pointer'},
      imageHeight: 48,
      imageWidth: 48,
      height: 55,
      buttonText: 'Perform Business Batch Lookup',
    };

    if (hasActiveFilters && isEmptyArray && !searchTerm) {
      return (
        <EmptyState
          body={
            'This filter did not return any data, try using different values.'
          }
          src={corruptedFile}
          noBtn
          height={35}
          customBtn={
            <button
              onClick={() => {
                setShowFilter(true);
                setCurrentFilters({
                  apps: undefined,
                  status: undefined,
                  country: undefined,
                });
                navigate(`?page=1`, {replace: true});
              }}
              className="text-brandBlue p-4 text-sm font-medium"
            >
              Clear filters
            </button>
          }
        />
      );
    }
    if (isEmptyArray && searchTerm) {
      return (
        <EmptyState
          body={`No results found for "${searchTerm}". Try a different search.`}
          src={corruptedFile}
          noBtn
        />
      );
    }
    if (
      (isEmptyArray || isNoDataInSandbox) &&
      !hasActiveFilters &&
      !searchTerm
    ) {
      return (
        <EmptyState
          body="No business batch historical record available"
          {...commonEmptyStateProps}
        />
      );
    }
    return null;
  };

  const tableCardData = buildTableCardData({
    count: batchBusinessLookup?.totalRecords,
    title: 'Total Business Batch Lookups',
  });

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Government Lookup</p>
      }
    >
      <EasyLookupModals
        open={openBatchLookup}
        setOpen={setOpenBL}
        lookupType="Batch"
        lookupCategory="Business"
        batch={true}
      />
      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <div className="flex items-center gap-2">
          <Breadcrumbs
            items={[
              {
                label: 'Business Batch Lookup',
                path: PAGES_URL.BUSINESS_LOOKUP,
              },
              {label: 'History'},
            ]}
          />
          {env !== 'Sandbox' && (
            <SecondaryButton
              buttonText="Filter"
              onClick={toggleFilterVisibility}
            />
          )}
        </div>

        {downloadBusinessLookup && (
          <PrimaryButton
            buttonText="Export"
            className="w-[100px]"
            onClick={handleExport}
            disabled={!downloadBusinessLookup || loading}
            title={
              !downloadBusinessLookup
                ? 'You do not have permission to perform this action'
                : ''
            }
            loading={loading}
          />
        )}
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2
          filters={filterDefinitions}
          onFilterChange={handleFilterChange}
        />
      </div>

      {children ?? (
        <>
          <OverviewCards
            cardData={tableCardData}
            overviewLoading={h_loading}
            className={`grid md:grid-cols-${
              tableCardData?.length > 3
                ? tableCardData?.length - 1
                : tableCardData?.length > 0
                ? tableCardData.length
                : 1
            } xl:grid-cols-${
              tableCardData?.length > 0 ? tableCardData.length : 1
            } gap-2 lg:gap-4 flex-wrap my-5`}
          />

          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                {batchBusinessTableHeader.map(({label, key}) => (
                  <th
                    key={key}
                    className={`${
                      label === 'Name'
                        ? 'pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap'
                        : 'pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap'
                    }`}
                  >
                    <div className="flex items-end gap-1 cursor-pointer">
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>

            <tbody className="text-sm bg-white">
              {(h_loading || loading || (env === 'Sandbox' && apiLoading)) && (
                <tr>
                  <td colSpan={batchBusinessTableHeader.length + 1}>
                    <Loader height={50} />
                  </td>
                </tr>
              )}
              {!(h_loading || (env === 'Sandbox' && apiLoading)) &&
                data?.length === 0 && (
                  <tr>
                    <td colSpan={batchBusinessTableHeader.length + 1}>
                      <EmptyStateComponent />
                    </td>
                  </tr>
                )}
              {!(h_loading || (env === 'Sandbox' && apiLoading)) &&
                data &&
                data?.map((record, i) => (
                  <tr
                    onClick={() => {
                      if (
                        record.status?.toLowerCase() !== 'completed' &&
                        record.status?.toLowerCase() !== 'complete'
                      )
                        return;
                      setOpenBL(true);
                      changeLookupPage(3);
                      env !== 'Sandbox' && getBatch(record?.id);
                    }}
                    className="border-b cursor-pointer border-grey60 hover:bg-gray-50"
                    key={record?.id || i}
                  >
                    <td className="p-4 pl-4 sm:pl-6 xl:pl-12 text-sm text-body whitespace-nowrap">
                      {record?.name || record?.file_name || '-'}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {`${Constants.lookups.getCountryAcronym(
                        record?.country,
                      )} ${capitalizeEveryWord(
                        getIDType(record?.model_type || record?.lookup_type),
                      )}`}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <div
                        className={`px-2 pt-[5px] pb-[3px] text-xs font-medium uppercase rounded-full w-fit ${
                          record?.status?.toLowerCase() === 'completed' ||
                          record?.status?.toLowerCase() === 'complete'
                            ? 'text-success20 bg-success'
                            : record?.status?.toLowerCase() === 'failed'
                            ? 'text-white bg-status-404'
                            : record?.status?.toLowerCase() === 'processing'
                            ? 'text-grey80 bg-grey4'
                            : 'text-white bg-brandBlue'
                        }`}
                      >
                        {checkStatus(record?.status) || '-'}
                      </div>
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {moment(record?.date_created || record?.createdAt).format(
                        'MMM DD,YYYY hh:mmA',
                      ) || '-'}
                    </td>
                    {(record?.status?.toLowerCase() === 'completed' ||
                      record?.status?.toLowerCase() === 'complete') && (
                      <td className="p-4 pl-0 whitespace-nowrap">
                        <div className="flex items-center gap-4">
                          <button
                            title="View Batch Details"
                            className="h-4 aspect-square"
                          >
                            <img src={eye} alt="View Batch" />
                          </button>
                        </div>
                      </td>
                    )}
                    {!(
                      record?.status?.toLowerCase() === 'completed' ||
                      record?.status?.toLowerCase() === 'complete'
                    ) && <td className="p-4 pl-0 whitespace-nowrap"></td>}
                  </tr>
                ))}
            </tbody>
          </TableLayout>

          {!(h_loading || (env === 'Sandbox' && apiLoading)) &&
            data?.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 -mx-8 px-8 border-t border-[#E4E3EC] py-[18px]">
                <PageNumber
                  currentPage={+currentPage}
                  limit={limit}
                  totalCount={batchBusinessLookup?.totalRecords}
                />
                <div className="flex items-center gap-5">
                  <PageLimit
                    onLimitChange={l => {
                      setLimit(l);
                      setCurrentPage(1);
                      const searchParams = new URLSearchParams(
                        window.location.search,
                      );
                      searchParams.set('page', '1');
                      searchParams.set('limit', l.toString());
                      navigate(`?${searchParams.toString()}`, {replace: true});
                    }}
                    total={batchBusinessLookup?.totalRecords}
                    length={batchBusinessLookup?.records?.length}
                    limit={limit}
                  />
                  <Pagination
                    total={
                      batchBusinessLookup &&
                      Math.ceil(batchBusinessLookup?.totalRecords / limit)
                    }
                    current={+currentPage}
                    onPageChange={activePage => {
                      pageClick(activePage);
                    }}
                  />
                </div>
              </div>
            )}
        </>
      )}
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(BusinessBatchLookup);
